import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReviewDto } from '@rp/contracts';
import { api } from '../lib/api';
import { Badge, Button, Card, Skeleton, Stars, Textarea } from '../components/ui';
import { PLATFORM_LABEL, formatDate, ratingColor, replyAttribution, STATUS_LABEL } from '../lib/format';
import { alert, confirm, haptic } from '../lib/telegram';
import { useSession } from '../hooks/session';

/**
 * Карточка отзыва и работа с ответом.
 *
 * Логика правки: ответ пишет ИИ, оператор его либо публикует, либо описывает
 * своими словами, что не так. Пожелание уходит в модель вместе с забракованным
 * вариантом — так следующая версия правит конкретное место, а не начинается с нуля.
 * Пожелание остаётся в поле после генерации: его можно дополнить и сгенерировать снова.
 *
 * Публикация вынесена в закреплённую панель внизу. Раньше она стояла в конце
 * длинной страницы и у отзыва с фотографиями оказывалась за пределами экрана —
 * главное действие приходилось искать прокруткой.
 */
export function ReviewPage() {
  const { can } = useSession();
  const { reviewId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [instruction, setInstruction] = useState('');
  const [refining, setRefining] = useState(false);

  const review = useQuery({ queryKey: ['review', reviewId], queryFn: () => api.review(reviewId) });
  const reply = review.data?.reply ?? null;

  // Локальный текст синхронизируется, когда с сервера приходит новая версия ответа.
  useEffect(() => {
    if (reply) setDraft(reply.text);
  }, [reply?.id, reply?.text]);

  // Пожелание, по которому сделан текущий вариант, показываем — его удобно дополнить.
  useEffect(() => {
    if (reply?.instruction) setInstruction(reply.instruction);
  }, [reply?.id]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['review', reviewId] });
    void queryClient.invalidateQueries({ queryKey: ['feed'] });
    void queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };

  const generate = useMutation({
    mutationFn: (options: { instruction?: string }) =>
      api.generateReply(reviewId, { regenerate: true, ...options }),
    onSuccess: (data) => {
      setDraft(data.reply?.text ?? '');
      setRefining(false);
      haptic.success();
      invalidate();
    },
    onError: (error: Error) => {
      haptic.error();
      alert(error.message);
    },
  });

  const save = useMutation({
    mutationFn: () => api.saveReply(reviewId, draft),
    onSuccess: () => {
      haptic.success();
      invalidate();
    },
    onError: (error: Error) => alert(error.message),
  });

  const publish = useMutation({
    mutationFn: () => api.publishReply(reviewId, draft),
    onSuccess: (data) => {
      if (data.reply?.status === 'FAILED') {
        haptic.error();
        alert(`Не удалось опубликовать: ${data.reply.error ?? 'неизвестная ошибка'}`);
      } else {
        haptic.success();
      }
      invalidate();
    },
    onError: (error: Error) => {
      haptic.error();
      alert(error.message);
    },
  });

  const skip = useMutation({
    mutationFn: () => api.skipReview(reviewId),
    onSuccess: () => {
      invalidate();
      navigate(-1);
    },
  });

  if (review.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-2 px-4 pt-3">
        <Skeleton className="h-44" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  if (!review.data) {
    return <p className="t-body p-8 text-center text-[var(--color-hint)]">Отзыв не найден</p>;
  }

  const data = review.data;
  const published = reply?.status === 'PUBLISHED';
  const busy = generate.isPending || save.isPending || publish.isPending;
  const edited = reply !== null && draft.trim() !== reply.text.trim();
  const editable = !published && can.reply;

  return (
    <div className="mx-auto max-w-lg">
      <div className={`px-4 pt-3 ${editable ? 'pb-[calc(var(--tab-bar-height)+84px)]' : 'pb-6'}`}>
        <ReviewBody review={data} />

        <Card>
          <header className="mb-3 flex items-center justify-between gap-2">
            <h2 className="t-headline">{published ? 'Опубликованный ответ' : 'Ответ организации'}</h2>
            {reply && !published && reply.revision > 1 && <Badge>вариант {reply.revision}</Badge>}
          </header>

          {!editable ? (
            <>
              {reply ? (
                <>
                  <p className="t-body whitespace-pre-line">{reply.text}</p>
                  <p className="t-caption mt-3 text-[var(--color-hint)]">
                    {replyAttribution(reply)}
                    {reply.publishedAt ? ` · ${formatDate(reply.publishedAt)}` : ''}
                  </p>
                </>
              ) : (
                <p className="t-subhead text-[var(--color-hint)]">Ответа пока нет.</p>
              )}
              {!published && !can.reply && (
                <p className="t-footnote mt-3 text-[var(--color-hint)]">
                  Отвечать на отзывы могут операторы и администраторы.
                </p>
              )}
            </>
          ) : (
            <>
              {!reply && !generate.isPending && (
                <p className="t-footnote mb-3 rounded-[10px] bg-[var(--color-surface)] px-3 py-2.5 leading-relaxed text-[var(--color-hint)]">
                  Черновик обычно появляется сам через минуту после отзыва. Можно сгенерировать
                  сейчас или написать ответ самому.
                </p>
              )}

              <Textarea
                rows={7}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Текст ответа"
              />
              <div className="t-caption mt-1.5 flex justify-between gap-3 text-[var(--color-hint)]">
                <span className="min-w-0 truncate">
                  {edited ? 'Изменено вручную' : reply ? replyAttribution(reply) : ''}
                </span>
                <span className="shrink-0 tabular-nums">{draft.length}</span>
              </div>

              {refining ? (
                <RefinePanel
                  value={instruction}
                  onChange={setInstruction}
                  busy={generate.isPending}
                  onCancel={() => setRefining(false)}
                  onSubmit={() => {
                    const text = instruction.trim();
                    if (text.length < 3) {
                      alert('Опишите, каким должен быть ответ');
                      return;
                    }
                    generate.mutate({ instruction: text });
                  }}
                />
              ) : (
                <>
                  {/*
                    Обе кнопки — про работу ИИ, поэтому стоят рядом и одинаково
                    тихо. Публикация решается внизу, отдельно от переписывания.
                  */}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        haptic.tap();
                        setRefining(true);
                      }}
                    >
                      Переписать
                    </Button>
                    <Button
                      variant="secondary"
                      loading={generate.isPending && !generate.variables?.instruction}
                      disabled={busy}
                      onClick={() => generate.mutate({})}
                    >
                      {reply ? 'Другой вариант' : 'Сгенерировать'}
                    </Button>
                  </div>

                  {reply?.instruction && (
                    <p className="t-caption mt-2 rounded-[10px] bg-[var(--color-surface)] px-3 py-2 leading-relaxed text-[var(--color-hint)]">
                      <span className="font-medium">Ваше пожелание:</span> {reply.instruction}
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </Card>

        {editable && (
          <button
            className="pressable t-footnote mt-3 w-full py-2 text-[var(--color-hint)]"
            disabled={busy}
            onClick={() => {
              void confirm('Пропустить этот отзыв без ответа?').then((confirmed) => {
                if (confirmed) skip.mutate();
              });
            }}
          >
            Пропустить без ответа
          </button>
        )}
      </div>

      {/*
        Панель действий закреплена внизу и полупрозрачна: текст ответа виден под
        ней, а решение всегда на расстоянии большого пальца.
      */}
      {editable && (
        <div className="material fixed inset-x-0 bottom-[var(--tab-bar-height)] z-30 border-t border-[var(--color-separator)] px-4 py-3">
          <div className="mx-auto flex max-w-lg gap-2">
            <Button
              className="flex-1"
              loading={publish.isPending}
              disabled={busy || draft.trim().length === 0}
              onClick={() => publish.mutate()}
            >
              Опубликовать
            </Button>
            {edited && (
              <Button
                variant="secondary"
                loading={save.isPending}
                disabled={busy || draft.trim().length === 0}
                onClick={() => save.mutate()}
              >
                Сохранить
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Поле, где оператор своими словами описывает, каким должен быть ответ. */
function RefinePanel({
  value,
  onChange,
  busy,
  onSubmit,
  onCancel,
}: {
  value: string;
  onChange: (value: string) => void;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const HINTS = [
    'извиниться и предложить связаться',
    'короче',
    'теплее, без официоза',
    'уточнить, что уже исправили',
  ];

  return (
    <div className="surface-in mt-3 rounded-[var(--radius-card)] bg-[var(--color-surface)] p-3">
      <p className="t-subhead mb-2 font-semibold">Как бы вы ответили?</p>
      <Textarea
        rows={3}
        value={value}
        autoFocus
        onChange={(event) => onChange(event.target.value)}
        placeholder="Обычными словами — ИИ перепишет ответ под ваше пожелание"
        className="bg-[var(--color-section)]"
      />

      <div className="mt-2 flex flex-wrap gap-1.5">
        {HINTS.map((hint) => (
          <button
            key={hint}
            onClick={() => {
              haptic.select();
              onChange(value.trim() ? `${value.trim()}, ${hint}` : hint);
            }}
            className="pressable t-caption rounded-full bg-[var(--color-section)] px-2.5 py-1 text-[var(--color-hint)]"
          >
            + {hint}
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <Button className="flex-1" loading={busy} onClick={onSubmit}>
          Сгенерировать
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

/** Сам отзыв: контекст для ответа, поэтому спокойнее по весу, чем редактор. */
function ReviewBody({ review }: { review: ReviewDto }) {
  const tone = ratingColor(review.rating);

  return (
    <Card className="mb-2">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span
          className="t-subhead inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold tabular-nums"
          style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
        >
          {review.rating.toFixed(1)}
          <Stars rating={review.rating} size={12} />
        </span>
        <span className="t-footnote text-[var(--color-hint)]">{PLATFORM_LABEL[review.platform]}</span>
        <Badge>{STATUS_LABEL[review.status]}</Badge>
      </header>

      <p className="t-headline">{review.authorName}</p>
      <p className="t-footnote mb-3 text-[var(--color-hint)]">
        {formatDate(review.publishedAt)} · {review.branch.name}
      </p>

      <p className="t-body whitespace-pre-line">
        {review.text || <span className="text-[var(--color-hint)]">Оценка без текста</span>}
      </p>

      {review.photos.length > 0 && (
        <div className="no-scrollbar -mx-1 mt-3 flex gap-2 overflow-x-auto px-1">
          {review.photos.map((photo) => (
            <img
              key={photo}
              src={photo}
              alt=""
              loading="lazy"
              className="h-24 w-24 shrink-0 rounded-[10px] object-cover"
            />
          ))}
        </div>
      )}

      {review.externalUrl && (
        <a
          href={review.externalUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="t-subhead mt-3 inline-block text-[var(--color-link)]"
        >
          Открыть на площадке ↗
        </a>
      )}
    </Card>
  );
}
